<template>
    <div id="manage-attendance-page">
        <!-- HEADER -->
        <div class="action-header">
            <div class="d-flex-wrap">
                <div class="search-container">
                    <div class="search-input">
                        <v-text-field
                            v-model="search"
                            prepend-inner-icon="search"
                            :placeholder="this.$vuetify.lang.t('$vuetify.manageAttendance.searchByEmployeeName')"
                            solo
                            height="40px"
                            @keyup.enter="searchAttendances()">
                        </v-text-field>
                        <v-btn
                            class="ml-3"
                            height="40px"
                            color="primary"
                            outlined
                            @click="searchAttendances()">
                            {{ $vuetify.lang.t('$vuetify.manageAttendance.search') }}
                        </v-btn>
                    </div>
                    <div class="filter-input">
                        <date-picker-range
                            class="mr-3 date-range-input"
                            v-model="dateRange" />

                        <div class="mr-3 select-filter">
                            <v-select
                                @change="searchAttendances()"
                                v-model="statusFilter"
                                :items="statusList"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                                height="40"
                                background-color="#ffffff">
                            </v-select>
                        </div>
                        <div class="mr-3 select-filter">
                            <v-select
                                @change="searchAttendances()"
                                v-model="bureauFilter"
                                :items="bureauList"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                                height="40"
                                background-color="#ffffff">
                            </v-select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- END HEADER -->

        <!-- DATATABLE -->
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalData"
                :items="items"
                :loading="loading">
                <template v-slot:[`item.action`]="{ item }">
                    <div class="action" v-if="canUpdate">
                        <i class="mr-3 far fa-edit" @click="editAttendanceStatus(item.id)"></i>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <!-- END DATATABLE -->

        <!-- PAGINATION -->
        <div
            v-if="pageCount"
            class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <!-- END PAGINATION -->
    </div>
</template>

<script>
import DatePickerRange from '@/components/DatePickerRange.vue'

export default {
    name: 'ManageAttendance',

    components: {
        DatePickerRange
    },

    data () {
        return {
            loading: false,
            search: '',
            pageCount: 1,
            page: 1,
            options: {},
            items: [],
            totalData: 0,
            dateRange: [this.$dateUtil.getCurrentDate(), this.$dateUtil.getCurrentDate()],
            bureauFilter: null,
            statusFilter: null,
            bureauList: [],
            statusList: [],
            canUpdate: false
        }
    },

    computed: {
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.employeeId'), value: 'employeeId', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.employeeName'), value: 'fullname', width: '25%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.bureau'), value: 'bureau', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.date'), value: 'date', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.checkIn'), value: 'checkin_at', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendance.status'), value: 'status', width: '15%' },
                { text: '', value: 'action', width: '8%', align: 'center', sortable: false }
            ]
        }
    },

    methods: {
        async initAttendance () {
            await this.getBureaus()
            await this.getStatus()
            await this.getAttendances()
        },

        async searchAttendances () {
            this.options.page = 1
            await this.getAttendances()
        },
        async getAttendances () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `attendances?page=${page}&itemsPerPage=${itemsPerPage}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            if (this.dateRange.length > 0) {
                url += `&dateRange=${JSON.stringify(this.dateRange)}`
            }

            if (this.statusFilter) {
                url += `&status=${this.statusFilter}`
            }

            if (this.bureauFilter) {
                url += `&bureau=${this.bureauFilter}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.items = res.data.data
                this.totalData = res.data.total
            }
            this.loading = false
        },

        async getBureaus () {
            const res = await this.axios.get('bureau/options')
            if (res && res.data && res.data.status === 'ok') {
                this.bureauList = res.data.data
            }
        },

        async getStatus () {
            const res = await this.axios.get('attendance/status/options')
            if (res && res.data && res.data.status === 'ok') {
                this.statusList = res.data.data
            }
        },

        editAttendanceStatus (id) {
            this.$router.push({ name: 'ManageAttendanceForm', params: { id } }, () => {})
        },

        actionStyle (checkinTime) {
            return checkinTime && checkinTime !== '-' ? 'action-enable' : 'action-disable'
        }
    },

    async mounted () {
        this.canUpdate = await this.hasPermission(this.$constant.permission.ATTENDANCE.UPDATE)
    },

    watch: {
        options: {
            async handler () {
                await this.initAttendance()
            }
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.initAttendance()
            },
            deep: true
        },
        dateRange () {
            if (this.dateRange.length === 0 || this.dateRange.length === 2) {
                this.searchAttendances()
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    .search-container {
        ::v-deep .v-input__control > .v-input__slot {
            min-height: auto !important;
            display: flex !important;
            align-items: center !important;
            box-shadow: none !important;
            border: 1px solid #a8a8a8 !important;
            width: 300px;
        }

        ::v-deep .search-input .v-input__control > .v-input__slot {
            width: 340px;
        }

        ::v-deep .date-range-input .v-input__control > .v-input__slot {
            width: 220px;
        }
    }

    ::v-deep .select-filter {
        .v-input__control > .v-input__slot {
            border: none !important;
            width: 200px;
        }

        .v-select__selection{
            line-height: 1.75;
        }
    }

    ::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details {
        margin: 0 !important;
    }

    .d-flex-wrap {
        display: flex;
        flex-wrap: wrap;
    }

    .date-range-input {
        width: 220px;
    }

    .action {
        display: flex;
        align-items: center;

        .action-enable i {
            cursor: pointer;
            color: #6c6c6c !important;
            font-weight: 400 !important;
            padding: 15px;
        }

        .action-disable i {
            pointer-events: none;
            cursor: not-allowed;
            color: #a8a8a8 !important;
            font-weight: 200 !important;
            padding: 15px;
        }
    }

    ::v-deep div[class*="select-filter"] .v-select__selection {
        line-height: 1.75;
    }
</style>
